import { Device } from "api/types/device";
import { useEffect, useState } from "react";
import { formatDate, formatDateToZ } from "util/datelib";
import { DeviceConfiguration } from "models/devices/configuration";
import { t } from "i18next";
import DeviceCard from "widgets/cards/device";
import DateRangePickerField from "widgets/shared/inputs/dateRangePicker";
import { dataAverageTimeSeries, dataMixed } from "api/data";
import { DataReqBody } from "api/types/data";
import useViewportDimensions from "hooks/viewport-dimensions";
import Table from "widgets/controls/tables/genericTable";
import defineAggregatesTablecolumns, {
  AggregatesPageTableModel,
} from "models/tables/aggregatesPage";
import JsonDialog from "widgets/dialogs/json";

type LooseValue = Date | [Date, Date] | null;

interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
}

const Aggregates: React.FC<GeneralProps> = ({ device, config }) => {
  const { dimensions } = useViewportDimensions();
  const [dateRange, setDateRange] = useState<LooseValue>(() => {
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);
    return [lastWeek, today];
  });

  const columns = defineAggregatesTablecolumns((info) => {
    const row = info.row.original;
    setJson(row);
    setIsOpen(true);
  });

  const [dataSource, setDataSource] = useState<AggregatesPageTableModel[]>([]);
  const [json, setJson] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchAggregates = async () => {
    setLoading(true);

    if (dateRange && Array.isArray(dateRange)) {
      const from = formatDateToZ(
        new Date(dateRange[0]),
        false,
        "day",
        device?.timezone
      ); // Start of selected date
      const to = formatDateToZ(
        new Date(dateRange[1]),
        true,
        "day",
        device?.timezone
      ); // End of selected date

      if (config.type === "average") {
        const req: DataReqBody = {
          snr: device?.snr,
          from: from,
          to: to,
          bucket: "hour",
          type: config?.units,
        };

        const { data, status, isLoading, error } = await dataAverageTimeSeries(
          req
        );
        setLoading(isLoading);

        if (!error && status && status === 200) {
          setDataSource(data);
        }
      } else {
        const req: DataReqBody = {
          snr: device?.snr,
          from: from,
          to: to,
          bucket: "hour",
        };

        const { data, status, isLoading, error } = await dataMixed(req);
        setLoading(isLoading);

        if (!error && status && status === 200) {
          setDataSource(data);
        }
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (device?.snr) {
      fetchAggregates();
    }
  }, [device, dateRange]);

  return (
    <>
      <JsonDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        jsonData={json}
        title={t("dialog.json.data.title")}
      />
      <DeviceCard
        title={t("card.device.database.aggregates.header")}
        subTitle={
          dateRange &&
          Array.isArray(dateRange) &&
          `${formatDate(dateRange[0], "ddMMMyyyy")} - ${formatDate(
            dateRange[1],
            "ddMMMyyyy"
          )}`
        }
        topRight={
          <div className="flex items-center gap-2">
            <DateRangePickerField
              id="date-range"
              onChange={(val) => {
                setDateRange(val);
              }}
              iconOnly
              sizes="xs"
              maxDate={new Date()}
              color="secondary"
            />
          </div>
        }
      >
        <div
          className="flex w-full items-center justify-center overflow-y-auto"
          style={{ height: dimensions.contentHeight - 180 }}
        >
          <Table
            offset={180}
            loading={loading}
            data={dataSource}
            columns={columns}
            compact
            totalRecords={dataSource.length}
          />
        </div>
      </DeviceCard>
    </>
  );
};

export default Aggregates;
